<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

import PoiBrowseCard from '@/components/map/PoiBrowseCard.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import { usePoiBrowse } from '@/composables/usePoiBrowse'
import {
  FLOATING_SHEET_BOTTOM_OFFSET,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
} from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * Mobile presentation of the basemap-POI "Add to" card: a draggable bottom sheet (peek / mid / full)
 * so the user can see the map and the destination list at the same time. Desktop uses a map popup
 * instead (see PoiBrowseLayer). Driven by the shared `selectedPoiCard` state.
 */
defineOptions({ name: 'PoiBrowseSheet' })

const { selectedPoiCard, clearSelectedPoiCard } = usePoiBrowse()

// Same bottom offset the other detail sheets use (RouteDetailSheet, collection drawer) — sits at the
// screen edge when the tab bar is hidden (collection/route detail level), not floating above a gap.
const floatingSheetBottom = inject<Ref<string>>(
  'floatingSheetBottomOffset',
  ref(FLOATING_SHEET_BOTTOM_OFFSET),
)

const viewportHeight = ref(
  typeof window === 'undefined' ? 844 : (window.visualViewport?.height ?? window.innerHeight),
)
const updateViewportHeight = () => {
  viewportHeight.value = window.visualViewport?.height ?? window.innerHeight
}

const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const height = viewportHeight.value
  const full = mobileSheetFullHeight(height)
  return {
    peek: 168,
    mid: Math.min(360, full - 40),
    full,
  }
})
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: 'mid',
  // Swipe the sheet down past peek to dismiss (as well as the card's own close button).
  onDismiss: clearSelectedPoiCard,
})

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(floatingSheetBottom.value),
  bottom: floatingSheetBottom.value,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition: sheet.isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

onMounted(() => window.visualViewport?.addEventListener('resize', updateViewportHeight))
onBeforeUnmount(() => window.visualViewport?.removeEventListener('resize', updateViewportHeight))
useEventListener(window, 'resize', updateViewportHeight)
</script>

<template>
  <Transition name="poi-browse-sheet">
    <div
      v-if="selectedPoiCard"
      class="pointer-events-auto fixed left-3 right-3 flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.14)]"
      :style="sheetStyle"
      data-testid="poi-browse-sheet"
    >
      <!-- Drag handle -->
      <div
        class="flex shrink-0 touch-none justify-center bg-white py-1.5"
        @pointerdown="sheet.onPointerDown"
        @pointermove="sheet.onPointerMove"
        @pointerup="sheet.onPointerUp"
        @pointercancel="sheet.onPointerUp"
      >
        <div class="h-1.5 w-12 rounded-full bg-gray-300"></div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <PoiBrowseCard
          :name="selectedPoiCard.name"
          :type-label="selectedPoiCard.typeLabel"
          :icon="selectedPoiCard.icon"
          :icon-color="selectedPoiCard.iconColor"
          :coords-text="selectedPoiCard.coordsText"
          :tags="selectedPoiCard.tags"
          :hidden-tag-count="selectedPoiCard.hiddenTagCount"
          :destinations="selectedPoiCard.destinations"
          bare
          :close="clearSelectedPoiCard"
        />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.poi-browse-sheet-enter-active,
.poi-browse-sheet-leave-active {
  transition:
    transform 200ms ease,
    opacity 200ms ease;
}
.poi-browse-sheet-enter-from,
.poi-browse-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .poi-browse-sheet-enter-active,
  .poi-browse-sheet-leave-active {
    transition: none;
  }
}
</style>
